import styled from 'styled-components'
import { theme } from 'antd'

const useJourneyColorVars = () => {
  const { token } = theme.useToken()
  return {
    '--journey-text': token.colorText,
    '--journey-muted': token.colorTextSecondary,
    '--journey-separator': token.colorBorderSecondary,
    '--journey-strong-separator': token.colorBorder,
    '--journey-message-bg': token.colorInfoBg,
    '--journey-accent': token.colorPrimary,
    '--journey-warning': token.colorWarning,
    '--journey-success': token.colorSuccess,
    '--journey-panel-bg': token.colorFillQuaternary
  }
}

const JourneyFrame = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 12px 0;
`

const SummaryRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px 16px;
  line-height: 22px;
`

const SummaryLabel = styled.div`
  flex: 0 0 170px;
  color: var(--journey-muted);
`

const SummaryValue = styled.div`
  flex: 1 1 320px;
  min-width: 0;
  color: ${({ $muted }) => ($muted ? 'var(--journey-muted)' : 'var(--journey-text)')};
  font-weight: ${({ $strong }) => ($strong ? 500 : 400)};
`

const Muted = styled.span`
  color: var(--journey-muted);
  font-size: ${({ $small }) => ($small ? '12px' : 'inherit')};
`

const SummaryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
`

const Panel = styled.div`
  padding: 8px 12px;
  border-radius: 6px;
  background: var(--journey-panel-bg);
  border: 1px solid var(--journey-separator);
`

const DayHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: baseline;
  padding: 10px 0 4px;
  margin-top: 4px;
  border-top: 1px solid var(--journey-separator);
  font-weight: 600;
`

const FoldedDay = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-height: 40px;
  margin-top: 4px;
  padding: 8px 0;
  border: 0;
  border-top: 1px solid var(--journey-separator);
  background: none;
  color: var(--journey-text);
  font: inherit;
  text-align: left;
  cursor: pointer;
`

const FoldedTotals = styled.span`
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  color: var(--journey-muted);
`

const GapLine = styled.div`
  padding: 6px 0 6px 96px;
  font-size: 12px;
  color: var(--journey-muted);
`

const OtherOrderLine = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  color: var(--journey-muted);

  &::before,
  &::after {
    content: '';
    flex: 1 1 24px;
    height: 1px;
    background: var(--journey-strong-separator);
  }
`

const StepGrid = styled.div`
  display: grid;
  grid-template-columns: 96px minmax(0, 1fr);
  align-items: start;
  margin: ${({ $tinted }) => ($tinted ? '2px 0' : '0')};
  padding: 4px 8px 4px 0;
  border-radius: 6px;
  background: ${({ $tinted }) => ($tinted ? 'var(--journey-message-bg)' : 'transparent')};
`

const Clock = styled.div`
  color: var(--journey-muted);
  font-variant-numeric: tabular-nums;
`

const StepBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`

const StepLine = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 8px;
`

const StepLabel = styled.span`
  font-weight: ${({ $strong }) => ($strong ? 500 : 400)};
`

const ProductLine = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 2px 12px;
  font-size: 13px;
  color: var(--journey-muted);
`

const ProductName = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: ${({ $inOrder }) => ($inOrder ? 'var(--journey-text)' : 'inherit')};
  font-weight: ${({ $inOrder }) => ($inOrder ? 500 : 400)};
`

const InnerSteps = styled.div`
  margin-top: 4px;
  padding-left: 12px;
  border-left: 2px solid var(--journey-separator);
`

const ChatList = styled.ol`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 4px 0 0;
  padding: 8px 12px;
  list-style: none;
  border-radius: 6px;
  background: var(--journey-panel-bg);
  border: 1px solid var(--journey-separator);
`

const ChatLine = styled.li`
  display: grid;
  grid-template-columns: 48px minmax(0, 1fr);
  gap: 8px;
`

const ChatText = styled.span`
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`

const OrderRowGrid = styled.div`
  display: grid;
  grid-template-columns: 96px minmax(0, 1fr);
  align-items: center;
  margin-top: 6px;
  padding: 10px 0;
  border-top: 2px solid var(--journey-accent);
  font-weight: 600;
`

const OrderDot = styled.span`
  display: inline-block;
  width: 10px;
  height: 10px;
  margin-right: 8px;
  border-radius: 999px;
  background: var(--journey-accent);
`

const EndRowGrid = styled.div`
  display: grid;
  grid-template-columns: 96px minmax(0, 1fr);
  align-items: center;
  margin-top: 6px;
  padding: 10px 0;
  border-top: 2px solid ${({ $paid }) => ($paid ? 'var(--journey-success)' : 'var(--journey-strong-separator)')};
  font-weight: 600;
`

const NewerOrderGrid = styled.div`
  display: grid;
  grid-template-columns: 96px minmax(0, 1fr);
  align-items: start;
  margin: 4px 0;
  padding: 8px 8px 8px 0;
  border-top: 1px dashed var(--journey-strong-separator);
  border-bottom: 1px dashed var(--journey-strong-separator);
`

export {
  useJourneyColorVars,
  JourneyFrame,
  SummaryRow,
  SummaryLabel,
  SummaryValue,
  Muted,
  SummaryList,
  TagRow,
  Panel,
  DayHeader,
  FoldedDay,
  FoldedTotals,
  GapLine,
  OtherOrderLine,
  StepGrid,
  Clock,
  StepBody,
  StepLine,
  StepLabel,
  ProductLine,
  ProductName,
  InnerSteps,
  ChatList,
  ChatLine,
  ChatText,
  OrderRowGrid,
  OrderDot,
  EndRowGrid,
  NewerOrderGrid
}
